import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { PaginatedResponse } from '../../types';
import { validatePaginationParams, fixNegativeSkipInUrl } from '../../utils/pagination';

// Configuration
export const API_CONFIG = {
  PRODUCTION_HOST: 'gimmighoul.ecomply.org',
  DEFAULT_TIMEOUT: 180000,
  ENDPOINTS: {
    HEALTH: '/health',
    PRICING_HEADERS: '/api/v1/pricing-headers',
    PLANS: '/api/v1/plans',
    PRICE_LISTS: '/api/v1/pricelists',
    CONTRACTS: '/api/v1/contracts',
    CONTRACT_LINES: '/api/v1/contract-lines',
    CONTRACT_RECIPIENTS: '/api/v1/contract-recipients',
    CUSTOMERS: '/api/v1/customers',
    PRICING_ITEMS: '/api/v1/pricing-items',
    SERVERS: '/api/v1/servers',
    USERS: '/api/v1/users',
    USER_MANAGEMENT: '/api/v1/user-management',
    SUPERVISORS: '/api/v1/supervisors',
    CONTROLLERS: '/api/v1/controllers',
    INVOICES: '/api/v1/invoices',
    BULK_INVOICES: '/api/v1/bulk-invoices',
    COMPANIES: '/api/v1/companies',
    STRIPE_CUSTOMERS: '/api/v1/stripe-customers'
  }
};

const isDev = import.meta.env.DEV;

// Helper functions
export const getApiUrl = (): string => {
  // Detect if we are running on a local development host
  const localHosts = ['localhost', '127.0.0.1', '0.0.0.0', '::1'];
  const isLocalHost = typeof window !== 'undefined' && localHosts.includes(window.location.hostname);

  // Allow explicit override through environment variable
  const envBaseUrl =
    import.meta.env.VITE_API_BASE_URL ||
    import.meta.env.VITE_API_URL;
  if (envBaseUrl) {
    return envBaseUrl as string;
  }

  // If we are in development mode OR running from a loop-back host
  // Use relative URLs to leverage the proxy configuration
  if (isDev || isLocalHost) {
    return '';
  }

  // In production, keep the baseURL empty for relative requests
  return '';
};

export const createPaginatedResponse = <T>(
  items: T[], 
  page: number, 
  pageSize: number
): PaginatedResponse<T> => {
  const hasMore = items.length === pageSize;
  const totalItems = hasMore ? page * pageSize + 1 : (page - 1) * pageSize + items.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  
  return {
    items,
    total: totalItems,
    page,
    pageSize,
    totalPages
  };
};

// Base API class
export class BaseApiService {
  protected api: AxiosInstance;

  // Shared axios instance to avoid multiple interceptor registrations across services
  private static sharedApi: AxiosInstance | null = null;
  private static interceptorsInitialized = false;
  private static cachedToken: string | null = null;

  // Public getter for axios instance
  get axiosInstance(): AxiosInstance {
    return this.api;
  }

  constructor() {
    this.api = BaseApiService.getSharedApi();
  }

  private static getSharedApi(): AxiosInstance {
    if (this.sharedApi) {
      return this.sharedApi;
    }

    const baseURL = getApiUrl();

    if (isDev) {
      // Minimal, non-noisy log for environment confirmation
      // Avoid logging large objects like headers/data for performance
      // eslint-disable-next-line no-console
      console.info('API init', { baseURL, env: import.meta.env.MODE, host: typeof window !== 'undefined' ? window.location.hostname : 'n/a' });
    }

    this.sharedApi = axios.create({
      baseURL,
      timeout: API_CONFIG.DEFAULT_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Important for CSRF cookie
    });

    // Initialize token cache once
    try {
      const lsToken = (typeof window !== 'undefined') ? (localStorage.getItem('access_token') || localStorage.getItem('token')) : null;
      this.cachedToken = lsToken;
    } catch {
      this.cachedToken = null;
    }

    // Keep cached token in sync when it changes in other tabs
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e: StorageEvent) => {
        if (e.key === 'access_token' || e.key === 'token') {
          BaseApiService.cachedToken = e.newValue;
        }
      });
    }

    // Setup interceptors only once on the shared instance
    if (!this.interceptorsInitialized) {
      this.setupInterceptors(this.sharedApi);
      this.interceptorsInitialized = true;
    }

    return this.sharedApi;
  }

  private static getCsrfTokenFromCookie(): string | null {
    if (typeof document === 'undefined') return null;
    const name = 'csrf_token=';
    const decodedCookie = decodeURIComponent(document.cookie || '');
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return null;
  }

  private static setupInterceptors(instance: AxiosInstance): void {
    // Request interceptor
    instance.interceptors.request.use(
      (config) => {
        // Fix any negative skip values in the URL before sending the request
        if (config.url) {
          const fixedUrl = fixNegativeSkipInUrl(config.url);
          if (fixedUrl !== config.url) {
            if (isDev) {
              // eslint-disable-next-line no-console
              console.warn('Negative skip fixed', { originalUrl: config.url, fixedUrl, method: config.method });
            }
            config.url = fixedUrl;
          }
        }
        
        // Also check params object
        if ((config as any).params?.skip !== undefined && (config as any).params.skip < 0) {
          if (isDev) {
            // eslint-disable-next-line no-console
            console.warn('Negative skip in params fixed', { originalSkip: (config as any).params.skip, url: config.url, method: config.method });
          }
          (config as any).params.skip = 0;
        }
        
        // Use cached token to avoid repeated localStorage reads
        let token = BaseApiService.cachedToken;
        if (!token && typeof window !== 'undefined') {
          try {
            token = localStorage.getItem('access_token') || localStorage.getItem('token');
            BaseApiService.cachedToken = token;
          } catch {
            token = null;
          }
        }
        if (token) {
          (config.headers as any).Authorization = `Bearer ${token}`;
        }
        
        // Add CSRF token for state-changing requests
        const method = config.method?.toUpperCase();
        if (method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
          const csrfToken = BaseApiService.getCsrfTokenFromCookie();
          if (csrfToken) {
            (config.headers as any)['X-CSRF-Token'] = csrfToken;
          }
        }
        
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    instance.interceptors.response.use(
      (response) => {
        if (isDev) {
          // eslint-disable-next-line no-console
          console.debug('API', response.status, response.config?.url);
        }
        return response;
      },
      (error) => {
        // Special handling for 422 errors with negative skip
        if (error.response?.status === 422) {
          const url = error.config?.url || '';
          const fullUrl = error.response?.config?.url || error.request?.responseURL || '';
          if (url.includes('skip=-') || fullUrl.includes('skip=-')) {
            if (isDev) {
              // eslint-disable-next-line no-console
              console.error('422 due to negative skip parameter', { url, fullUrl, method: error.config?.method });
            }
          }
        }
        
        // Handle aborted requests
        if (error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED') {
          if (isDev) {
            // eslint-disable-next-line no-console
            console.warn('Request aborted', error.config?.url);
          }
          return Promise.reject(error);
        }
        
        if (error.response?.status === 401) {
          const isLoginPage = typeof window !== 'undefined' && window.location.pathname.includes('/login');
          const isRefreshEndpoint = error.config?.url?.includes('/api/v1/user-auth/refresh');
          const isMeEndpoint = error.config?.url?.includes('/api/v1/user-auth/me');
          const isHealthEndpoint = error.config?.url?.includes('/health');
          if (!isLoginPage && !isRefreshEndpoint && !isMeEndpoint && !isHealthEndpoint) {
            if (isDev) {
              // eslint-disable-next-line no-console
              console.warn('401 detected in API layer');
            }
          }
        }
        
        if (error.response?.status === 403 && error.response?.data?.detail === 'CSRF validation failed') {
          if (isDev) {
            // eslint-disable-next-line no-console
            console.error('CSRF validation failed, attempting refresh');
          }
          BaseApiService.refreshCsrfToken();
        }
        
        if (isDev) {
          const errorData = error.response?.data;
          // eslint-disable-next-line no-console
          console.error('API Error', {
            status: error.response?.status,
            message: errorData?.message || error.message,
            detail: errorData?.detail,
            url: error.config?.url,
            method: error.config?.method
          });
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Method to refresh CSRF token by making a GET request
  private static async refreshCsrfToken(): Promise<void> {
    try {
      await this.getSharedApi().get('/health');
    } catch (error) {
      if (isDev) {
        // eslint-disable-next-line no-console
        console.error('Failed to refresh CSRF token', error);
      }
    }
  }

  // Generic HTTP methods
  protected async get<T>(url: string): Promise<T> {
    const fixedUrl = fixNegativeSkipInUrl(url);
    
    if (fixedUrl !== url && isDev) {
      // eslint-disable-next-line no-console
      console.warn('Negative skip fixed in GET', { originalUrl: url, fixedUrl });
    }
    
    const response: AxiosResponse<T> = await this.api.get(fixedUrl);
    return response.data;
  }

  protected async post<T, D = any>(url: string, data?: D): Promise<T> {
    const response: AxiosResponse<T> = await this.api.post(url, data);
    return response.data;
  }

  protected async put<T, D = any>(url: string, data?: D): Promise<T> {
    const response: AxiosResponse<T> = await this.api.put(url, data);
    return response.data;
  }

  protected async delete<T>(url: string): Promise<T> {
    const response: AxiosResponse<T> = await this.api.delete(url);
    return response.data;
  }

  protected async getBlob(url: string): Promise<Blob> {
    const fixedUrl = fixNegativeSkipInUrl(url);
    
    if (fixedUrl !== url && isDev) {
      // eslint-disable-next-line no-console
      console.warn('Negative skip fixed in GET blob', { originalUrl: url, fixedUrl });
    }
    
    const response: AxiosResponse<Blob> = await this.api.get(fixedUrl, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Pagination helper
  protected async getPaginated<T>(
    endpoint: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<PaginatedResponse<T>> {
    const { page: validPage, pageSize: validPageSize, skip, limit } = validatePaginationParams(page, pageSize);
    
    if ((page !== validPage || pageSize !== validPageSize) && isDev) {
      // eslint-disable-next-line no-console
      console.warn('Pagination parameters adjusted', { 
        endpoint,
        original: { page, pageSize },
        adjusted: { page: validPage, pageSize: validPageSize, skip }
      });
    }
    
    const normalizedEndpoint = endpoint.endsWith('/') ? endpoint.slice(0, -1) : endpoint;
    const url = `${normalizedEndpoint}/?skip=${skip}&limit=${limit}`;
    const items = await this.get<T[]>(url);
    return createPaginatedResponse(items, validPage, validPageSize);
  }

  // Get base URL for direct access
  getBaseUrl(): string {
    return this.api.defaults.baseURL || '';
  }
}
